/**
 * 1.4.0 asset groups, stream G3: the supplier's My deliveries grouped (portal/pages/deliveries.tsx): a group's
 * deliveries together under "Series › Season" (PortalDelivery.group), in episode order, so a season reads as one job;
 * the rest as before. The server puts them together and in order (withDeliveryGroups); this draws the headings.
 * Strings: portal-strings/groups-g3.ts (keys under groups.). G3's file.
 */
import type { ReactNode } from 'react';
import { t } from '../i18n/en';
import type { PortalDelivery } from '../types';

interface Run { groupId: string | null; path: string[]; items: PortalDelivery[] }

/** Consecutive deliveries of one group as one run; deliveries without a group each their own. */
export function deliveryRuns(items: PortalDelivery[]): Run[] {
  const runs: Run[] = [];
  for (const d of items) {
    const g = d.group ?? null;
    const last = runs[runs.length - 1];
    if (g && last && last.groupId === g.groupId) last.items.push(d);
    else runs.push({ groupId: g?.groupId ?? null, path: g?.path ?? [], items: [d] });
  }
  return runs;
}

export function GroupedDeliveries({ items, render }: { items: PortalDelivery[]; render: (d: PortalDelivery) => ReactNode }) {
  return (
    <div className="p-stack">
      {deliveryRuns(items).map((run) => (run.groupId === null ? run.items.map((d) => render(d)) : (
        <section key={`group:${run.groupId}:${run.items[0]!.id}`} className="p-delivery-group" data-testid="portal-delivery-group" aria-label={run.path.join(' › ')}>
          <header className="p-delivery-group-head">
            <h2>{t('groups.heading', { path: run.path.join(' › ') })}</h2>
            <span className="p-small p-muted">{run.items.length === 1 ? t('groups.countOne') : t('groups.count', { n: run.items.length })}</span>
          </header>
          {run.items.map((d) => (
            <div key={d.id} className="p-delivery-group-item" data-testid="portal-delivery-group-item">
              {d.group?.number != null && <span className="p-delivery-number">{t('groups.number', { n: d.group.number })}</span>}
              {render(d)}
            </div>
          ))}
        </section>
      )))}
    </div>
  );
}
